import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from 'react';

const ParentModal = ({id, onClose,}: {id?: string; onClose: () => void;}) => {
	const API_URL = import.meta.env.VITE_API_URL;
	const queryClient = useQueryClient();
	const [parent, setParent] = useState({name: "",	email: "", phone: ""});
	const [parentError, setParentError] = useState({name: "", email: "", phone: ""});

	const { data : parentData } = useQuery({
		queryKey: ['parent', id],
		queryFn: async () => {
			if (!id) return null;
			
			const params = new URLSearchParams();
			params.append("pr_id", id);

			const response = await fetch(`${API_URL}/bbs/ajax.get_parent.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: params,
				credentials: "include",
			});

			if (!response.ok) {
				throw new Error("네트워크 오류");
			}

			return response.json();
		},
		enabled: !!id,
	});
	const parentItem = parentData?.data ?? null;

	const mutation = useMutation({
		mutationFn: async (parentData: {name: string; email: string; phone: string;}) => {
			const params = new URLSearchParams();
			if (id) params.append("pr_id", id);
			params.append("name", parentData.name);
			params.append("email", parentData.email);
			params.append("phone", parentData.phone);

			const response = await fetch(`${API_URL}/bbs/ajax.update_parent.php`, {
					method: "POST",
					headers: {
						"Content-Type": "application/x-www-form-urlencoded",
					},
					body: params,
					credentials: "include",
				}
			);

			if (!response.ok) {
				throw new Error("네트워크 오류");
			}

			return response.json();
		},
		onSuccess: (response) => {
			if (!response.success) {
				switch (response.code) {
					case "INVALID_REQUEST":
						alert("필수 필드값이 입력되지 않았습니다.");
						break;
					default:
						alert("알 수 없는 오류가 발생했습니다.");
						break;
				}
				return;
			}

			if(id) alert("보호자 정보가 수정되었습니다.");
			else alert("보호자가 등록되었습니다.");

			queryClient.invalidateQueries({queryKey: ['member']});
			if (id) queryClient.invalidateQueries({queryKey: ['parent', id]});
			onClose();
		},
		onError: (error: any) => {
			console.log(error.message);
		},
	});

	const handleParentValidate = (field: string, value: string) => {
		let validValue = value;
		let errorMsg = "";

		if (field === "name") {
			validValue = value.replace(/[^a-zA-Z가-힣ㄱ-ㅎㅏ-ㅣ]/g, "");

			if (!validValue.trim()) {
				errorMsg = "이름을 입력해주세요.";
			}
		}

		if (field === "email") {
			validValue = value.trim();

			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

			if (!emailRegex.test(validValue)) {
				errorMsg = "올바른 이메일 형식이 아닙니다.";
			}
		}

		if (field === "phone") {
			let numbers = value.replace(/[^0-9]/g, "");

			if (numbers.length < 4) {
				validValue = numbers;
			} else if (numbers.length < 8) {
				validValue = `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
			} else {
				validValue =
					`${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
			}

			if (numbers.length < 10) {
				errorMsg = "올바른 연락처를 입력해주세요.";
			}
		}

		return {
			value: validValue,
			error: errorMsg,
		};
	};

	const handleParentComplete = () => {
		const nameValidation = handleParentValidate("name", parent.name);
		const emailValidation = handleParentValidate("email", parent.email);
		const phoneValidation = handleParentValidate("phone", parent.phone);
		setParentError({name: nameValidation.error, email: emailValidation.error, phone: phoneValidation.error});
		if (nameValidation.error || emailValidation.error || phoneValidation.error) return;

		if (id && !confirm("정말로 변경하시겠습니까?")) return;

		mutation.mutate(parent);
	};

	const handleParentChange = (field: string, value: string) => {
		const validated = handleParentValidate(field, value);

		setParent((prev) => ({
			...prev,
			[field]: validated.value,
		}));

		setParentError((prev) => ({
			...prev,
			[field]: validated.error,
		}));
	};

	useEffect(() => {
		if (parentItem) {
			setParent({
				name: parentItem.name ?? "",
				email: parentItem.email ?? "",
				phone: parentItem.phone ?? "",
			});
		}
	}, [parentItem]);

	return (
		<div className="fixed inset-0 bg-black/70 z-[1000] flex items-center justify-center p-5 ">
			<div className="p-5 border rounded-xl bg-white space-y-2 shadow-sm min-w-[300px] max-w-[400px]">
				<label  className="block text-sm font-semibold text-gray-700 mb-1">
					이름
				</label>
				<input
					type="text"
					placeholder="이름을 입력해주세요."
					value={parent?.name}
					onChange={(e) => handleParentChange('name', e.target.value)}
					className={`
						w-full px-4 py-3 rounded-lg border text-sm
						${parentError.name
							? "border-red-500 focus:border-red-500"
							: "border-gray-300 focus:border-blue-500"
						}
					`}
				/>
				{parentError.name && (
					<p className="text-sm text-red-500 mt-1">
						{parentError.name}
					</p>
				)}
				<label className="block text-sm font-semibold text-gray-700 mb-1">
					이메일
				</label>
				<input
					type="text"
					placeholder="이메일을 입력해주세요."
					value={parent?.email}
					onChange={(e) => handleParentChange('email', e.target.value)}
					className={`
						w-full px-4 py-3 rounded-lg border text-sm
						${parentError.email
							? "border-red-500 focus:border-red-500"
							: "border-gray-300 focus:border-blue-500"
						}
					`}
				/>
				{parentError.email && (
					<p className="text-sm text-red-500 mt-1">
						{parentError.email}
					</p>
				)}
				<label className="block text-sm font-semibold text-gray-700 mb-1">
					연락처
				</label>
				<input
					type="text"
					placeholder="연락처를 입력해주세요."
					value={parent?.phone}
					onChange={(e) => handleParentChange('phone', e.target.value)}
					className={`
						w-full px-4 py-3 rounded-lg border text-sm
						${parentError.phone
							? "border-red-500 focus:border-red-500"
							: "border-gray-300 focus:border-blue-500"
						}
					`}
				/>
				{parentError.phone && (
					<p className="text-sm text-red-500 mt-1">
						{parentError.phone}
					</p>
				)}
				<div className="flex gap-2">
					<button
						type="button"
						onClick={onClose}
						className="w-full px-4 py-3 rounded-lg text-sm font-semibold transition whitespace-nowrap mt-2 bg-gray-200 transition-colors hover:bg-gray-300"
			        >
			            닫기
			        </button>
					<button
						type="button"
						onClick={handleParentComplete}
						disabled={!parent?.name || !parent?.email || !parent?.phone}
						className={`w-full px-4 py-3 rounded-lg text-sm font-semibold transition whitespace-nowrap mt-2
			                ${parent?.name && parent?.email && parent?.phone
			                	? "bg-purple-600 text-white hover:bg-purple-700"
			                	: "bg-gray-200 text-gray-400 cursor-not-allowed"}
			                	`}
			                >
			            입력 완료
			        </button>
				</div>
	        </div>
	    </div>
	);
}

export default ParentModal;